import { sortBy } from 'lodash';
import type { ReactNode } from 'react';

import Box from '@mui/material/Box';

import { TotalModeEnum } from '@e-socna/data-layer';
import { EMPTY_ARRAY } from '@e-socna/empty';
import type { FragmentType, GroupingModeEnum, Id } from '@e-socna/data-layer';

import { buildGroupTotalCalculationMap } from '../../utils/build-group-total-calculation-map.util';

import { ServiceUsagesGroupBanner } from './GroupBanner';
import type {
  ServiceUsagesTotalCardCmp_GroupTotalCalculation_Fragment,
  ServiceUsagesTotalCardCmp_TotalCalculation_Fragment,
} from './TotalCard';
import { ServiceUsagesGlobalTotal } from './GlobalTotal';
import {
  getTotalCalculationErrorMessages,
  shouldShowGlobalTotal,
} from './TotalCard';
import type { ListProps } from './types/props.type';
import type {
  GlobalTotalOptionsGroup,
  GlobalTotalOptionsSection,
} from './utils/global-total-option-items.util';
import type { OptionGroupsSelectionControl } from './types/option-groups-selection-control.type';

// What a group of a pricing shows, whatever its usages.
export interface ServiceUsagesLayoutGroup {
  readonly _id: Id;
  readonly groupingMode: GroupingModeEnum;
  readonly index: number;
  readonly isOptional?: boolean;
  readonly label?: string | null;
  readonly totalMode?: TotalModeEnum;
}

// The parts of the usages list of a group decided by the layout of the groups.
export type ServiceUsagesGroupSlots = Required<
  Pick<
    ListProps<true>,
    | 'groupBanner'
    | 'isInGroup'
    | 'maskAmounts'
    | 'showInheritanceAlert'
    | 'showTotalCalculation'
  >
> &
  Pick<
    ListProps<true>,
    'globalTotal' | 'globalTotalErrorMessages' | 'totalCalculation'
  >;

type TotalCalculation = FragmentType<
  typeof ServiceUsagesTotalCardCmp_TotalCalculation_Fragment
> | null;

export type ServiceUsagesGroupsLayoutProps = Readonly<{
  // Groups priced elsewhere but part of the global total (the groups of the
  // sub-missions of a collaboration): offered in its option checkboxes.
  additionalTotalGroups?: readonly GlobalTotalOptionsGroup[];
  // Shown between the global total and the groups (the pricing of the
  // sub-missions of a collaboration), the global total then leading the page.
  leadingContent?: ReactNode;
  groupTotalCalculations?:
    | readonly FragmentType<
        typeof ServiceUsagesTotalCardCmp_GroupTotalCalculation_Fragment
      >[]
    | null;
  selectedOptionGroupIds?: readonly Id[] | null;
  // The section of the displayed groups, shown in their banners.
  groupSectionLabel?: string;
  showInheritanceAlert?: boolean;
  totalCalculation?: TotalCalculation;
  // Nests the option checkboxes of the global total by priced mission.
  totalSections?: readonly GlobalTotalOptionsSection[];
  // Saves the option choice of the global total on the server.
  optionGroupsSelection?: OptionGroupsSelectionControl;
}>;

type LayoutProps<G extends ServiceUsagesLayoutGroup> =
  ServiceUsagesGroupsLayoutProps &
    Readonly<{
      groups: readonly G[];
      renderGroupUsages: (
        group: G,
        slots: ServiceUsagesGroupSlots,
      ) => ReactNode;
    }>;

// The groups of a pricing, each with its banner and total, under the global
// total and its option choice, whether the pricing is live or frozen.
export const ServiceUsagesGroupsLayout = <G extends ServiceUsagesLayoutGroup>({
  additionalTotalGroups = EMPTY_ARRAY,
  groupSectionLabel,
  groupTotalCalculations,
  groups,
  leadingContent,
  optionGroupsSelection,
  renderGroupUsages,
  selectedOptionGroupIds,
  showInheritanceAlert = true,
  totalCalculation,
  totalSections,
}: Readonly<LayoutProps<G>>) => {
  const groupTotalCalculationMap = buildGroupTotalCalculationMap(
    groupTotalCalculations,
  );
  const totalGroups = [...additionalTotalGroups, ...groups];

  const globalTotal =
    totalCalculation &&
    shouldShowGlobalTotal(totalGroups.length, totalCalculation) ? (
      <ServiceUsagesGlobalTotal
        options={{
          groupTotalCalculationMap,
          groups: totalGroups,
          initialSelectedOptionGroupIds: selectedOptionGroupIds,
          sections: totalSections,
          selectionControl: optionGroupsSelection,
        }}
        totalCalculation={totalCalculation}
      />
    ) : undefined;
  const hasLeadingContent = leadingContent !== undefined;

  return (
    <Box sx={{ width: '100%' }}>
      {hasLeadingContent ? (
        <>
          <Box sx={{ pt: 2 }}>{globalTotal}</Box>
          {leadingContent}
        </>
      ) : null}
      {sortBy(groups, 'index').map((group, position) => (
        <Box component="section" key={group._id} sx={{ mb: 3 }}>
          {renderGroupUsages(group, {
            globalTotal:
              position === 0 && !hasLeadingContent ? globalTotal : undefined,
            globalTotalErrorMessages:
              totalGroups.length === 1
                ? getTotalCalculationErrorMessages(totalCalculation)
                : undefined,
            groupBanner: (
              <ServiceUsagesGroupBanner
                editionMode={false}
                groupingMode={group.groupingMode}
                isOptional={group.isOptional}
                label={group.label}
                sectionLabel={groupSectionLabel}
              />
            ),
            isInGroup: true,
            maskAmounts: group.totalMode === TotalModeEnum.Manual,
            showInheritanceAlert: position === 0 && showInheritanceAlert,
            showTotalCalculation: groupTotalCalculationMap.has(group._id),
            totalCalculation: groupTotalCalculationMap.get(group._id),
          })}
        </Box>
      ))}
    </Box>
  );
};
